import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const translations = {
  en: {
    // App
    "app.name": "Shubh Finance", "app.subtitle": "Management System",
    // Nav
    "nav.dashboard": "Dashboard", "nav.myLoans": "My Loans", "nav.emiSchedule": "EMI Schedule",
    "nav.payEMI": "Pay EMI", "nav.statements": "Statements", "nav.myCustomers": "My Customers",
    "nav.proposals": "Proposals", "nav.analytics": "Analytics", "nav.settings": "Settings",
    "nav.users": "Users", "nav.customers": "Customers", "nav.auditLogs": "Audit Logs",
    "nav.smsLogs": "SMS Logs", "nav.logout": "Logout",
    "nav.collections": "Payment Records", "nav.collectPayment": "Collect Payment",
    "nav.myReceipts": "My Receipts", "nav.paymentChannels": "Payment Channels",
    "nav.paymentVerification": "Payment Verification", "nav.myPayments": "My Payments",
    "nav.collectionIntelligence": "Collection Intelligence", "nav.agentPerformance": "Agent Performance",
    "nav.ledger": "Loan Ledger", "nav.bulkImport": "Bulk Import", "nav.importStaging": "Import Staging", "nav.dataQuality": "Data Quality", "nav.training": "Training Center",
    "nav.commissionRules": "Commission Rules", "nav.commissionLedger": "Commission Ledger", "nav.leaderboard": "Leaderboard", "nav.myCommissions": "My Commissions",
    "nav.overdueWaivers": "Overdue Waivers",
    "nav.interestOnlyAccounts": "Interest Only",
    "nav.partPaymentControls": "Part Payment",
    "nav.disbursements": "Disbursements",
    "nav.systemHealth": "System Health",
    "nav.duplicateResolution": "Duplicate Resolution",
    "nav.agentLedger": "Agent Ledger",
    "nav.customerLedger": "Customer Ledger",
    "nav.accDashboard": "Acc. Dashboard",
    "nav.bankAccounts": "Bank Accounts",
    "nav.cashAccounts": "Cash Accounts",
    "nav.journalEntries": "Journal Entries",
    // Roles
    "role.superadmin": "Super Admin", "role.admin": "Admin", "role.agent": "Agent", "role.customer": "Customer",
    // Common
    "common.save": "Save", "common.cancel": "Cancel", "common.close": "Close", "common.delete": "Delete",
    "common.create": "Create", "common.back": "Back", "common.viewAll": "View all", "common.submit": "Submit",
    "common.loading": "Loading...", "common.search": "Search", "common.noData": "No data found",
    "common.confirm": "Confirm", "common.yes": "Yes", "common.no": "No", "common.edit": "Edit",
    "common.download": "Download", "common.upload": "Upload", "common.refresh": "Refresh",
    "common.filter": "Filter", "common.add": "Add", "common.saveChanges": "Save Changes",
    "common.selectLoan": "Select Loan", "common.language": "Language",
    "common.darkMode": "Dark Mode", "common.lightMode": "Light Mode",
    // Auth
    "auth.login": "Login", "auth.username": "Username", "auth.password": "Password",
    "auth.loginBtn": "Sign In", "auth.loginTitle": "Welcome Back",
    "auth.loginSubtitle": "Sign in to your account",
    // Status
    "status.active": "Active", "status.pending": "Pending", "status.approved": "Approved",
    "status.rejected": "Rejected", "status.overdue": "Overdue", "status.paid": "Paid",
    "status.unpaid": "Unpaid", "status.closed": "Closed", "status.dueSoon": "Due soon",
    "status.waived": "Waived",
    // Customer Dashboard
    "custDash.myLoans": "My Loans", "custDash.welcomeBack": "Welcome back",
    "custDash.activeLoans": "Active Loans", "custDash.nextEMI": "Next EMI",
    "custDash.totalPaid": "Total Paid", "custDash.overdueCharges": "Overdue Charges",
    "custDash.activeLoan": "Active Loan", "custDash.noLoans": "No loans yet",
    "custDash.contactAgent": "Contact your agent to apply for a loan",
    "custDash.viewSchedule": "View full repayment schedule",
    "custDash.downloadPDFCSV": "Download PDF / CSV",
    "custDash.payNowSubtext": "pending",
    // Loan fields
    "loan.id": "Loan ID", "loan.type": "Type", "loan.principal": "Principal",
    "loan.emiAmount": "EMI Amount", "loan.frequency": "Frequency", "loan.tenure": "Tenure",
    "loan.interestRate": "Interest Rate", "loan.totalEmis": "Total EMIs",
    "loan.create": "Create Loan", "loan.startDate": "Start Date", "loan.purpose": "Purpose",
    "loan.customOverdueRate": "Custom Overdue Rate (₹/day)",
    "loan.customOverdueRateHint": "Set per-customer overdue rate. Leave blank to use system default.",
    "loan.interestRateLabel": "Interest Rate (% per month)",
    "loan.principalLabel": "Principal (₹)", "loan.tenureLabel": "Tenure (months)",
    "loan.frequencyLabel": "EMI Frequency", "loan.loanType": "Loan Type",
    "loan.months": "months", "loan.pm": "p.m.",
    "loan.paid": "paid", "loan.total": "total",
    "loan.noLoans": "No loans yet",
    // Frequencies
    "freq.daily": "Daily", "freq.monthly": "Monthly", "freq.bi_monthly": "15-15 Days", "freq.quarterly": "Quarterly",
    "freq.half_yearly": "Half Yearly", "freq.yearly": "Yearly",
    // EMI Schedule
    "emi.schedule": "EMI Schedule", "emi.viewSchedule": "View your complete repayment schedule",
    "emi.number": "#", "emi.dueDate": "Due Date", "emi.amount": "EMI",
    "emi.principal": "Principal", "emi.interest": "Interest",
    "emi.overdueDays": "Overdue Days", "emi.overdueCharges": "Overdue Charges",
    "emi.status": "Status", "emi.daysLeft": "days left", "emi.overdue": "overdue",
    "emi.noSchedule": "No EMI schedule found", "emi.fullSchedule": "Full Schedule",
    "emi.paidCount": "Paid", "emi.pendingCount": "Pending", "emi.overdueCount": "Overdue",
    "emi.afterDue": "per day after due",
    "emi.action": "Action", "emi.memo": "Memo", "emi.markPaid": "Mark Paid",
    // Pay EMI
    "pay.title": "Pay EMI", "pay.subtitle": "View your upcoming payments and overdue charges",
    "pay.payNow": "Pay Now", "pay.paymentDetails": "Payment Details",
    "pay.howToPay": "How to Pay", "pay.gotIt": "Got It", "pay.totalPayable": "Total Payable",
    "pay.emiAmount": "EMI Amount", "pay.allPaid": "All EMIs Paid!",
    "pay.upToDate": "Your loan is up to date. Great job!",
    "pay.pendingEmis": "All Pending EMIs",
    "pay.step1": "1. Contact your assigned agent to collect the payment",
    "pay.step2": "2. Pay via Cash, UPI, Bank Transfer, or Cheque",
    "pay.step3": "3. Your agent will mark the payment in the system",
    "pay.step4": "4. You will receive an SMS confirmation",
    "pay.additionalPenalty": "Additional penalty will be added for each additional day of delay.",
    "pay.nextEMIDue": "Next EMI Due", "pay.overdueEMI": "Overdue EMI",
    "pay.amount": "Amount", "pay.daysLeft": "Days Left", "pay.totalDue": "Total Due",
    // Overdue
    "overdue.penaltyInfo": "Overdue Penalty Information",
    "overdue.penaltyText": "A penalty will be charged for each day an EMI remains unpaid after its due date.",
    "overdue.perDay": "/day", "overdue.immediateAttention": "Overdue EMIs - Immediate Attention Required",
    "overdue.haveOverdue": "You have overdue EMI(s)",
    "overdue.totalCharges": "with total charges of",
    "overdue.contactAgent": "Contact your agent to pay immediately.",
    "overdue.latePenalty": "Late Payment Charges",
    "overdue.latePenaltyText": "A daily penalty is applicable for each day an EMI is not paid after its due date. This rate is set by your loan administrator.",
    // Statements
    "stmt.title": "Statements", "stmt.subtitle": "Download your loan payment statements in PDF or CSV format",
    "stmt.noLoans": "No loans found",
    "stmt.pdfStatement": "PDF Statement", "stmt.csvExport": "CSV Export",
    "stmt.downloadPDF": "PDF", "stmt.downloadCSV": "CSV",
    "stmt.dateRange": "Date Range", "stmt.from": "From", "stmt.to": "To",
    // Agent Dashboard
    "agent.myDashboard": "My Dashboard", "agent.welcome": "Welcome",
    "agent.newCustomer": "New Customer", "agent.recentCustomers": "Recent Customers",
    "agent.upcomingEmis": "Upcoming EMIs", "agent.pendingProposals": "Pending Proposals",
    "agent.customersOnboarded": "Onboarded by you", "agent.currentlyRunning": "Currently running",
    "agent.awaitingApproval": "Awaiting approval", "agent.proposalsApproved": "Proposals approved",
    "agent.noCustomers": "No customers yet. Onboard your first customer!",
    "agent.addCustomer": "Add Customer", "agent.noUpcomingEmis": "No upcoming EMIs",
    "agent.nextDueDates": "Next due dates", "agent.noPendingProposals": "No pending proposals",
    "agent.activeLoans": "Active Loans", "agent.approved": "Approved",
    // Super Admin Dashboard
    "sa.dashboard": "Overview", "sa.totalCustomers": "Total Customers",
    "sa.activeLoans": "Active Loans", "sa.totalOutstanding": "Total Outstanding",
    "sa.overdueLoans": "Overdue Loans", "sa.recentLoans": "Recent Loans",
    "sa.agentPerformance": "Agent Performance",
    // Admin Dashboard
    "admin.dashboard": "Dashboard", "admin.myAgents": "My Agents",
    "admin.totalCustomers": "Total Customers",
    // Settings
    "settings.title": "Settings", "settings.subtitle": "Configure system-wide settings",
    "settings.overdueCharges": "Overdue Charges", "settings.enableOverdue": "Enable Overdue Charges",
    "settings.disableHint": "When disabled, no charges apply to any customer",
    "settings.defaultRate": "Default Rate (₹ per day)",
    "settings.workflow": "Workflow", "settings.requireApproval": "Require Proposal Approval",
    "settings.approvalHint": "New loans require Super Admin approval before activation",
    "settings.rolePermissions": "Role Permissions",
    "settings.loanTypes": "Loan Types & Documents", "settings.addType": "Add Type",
    "settings.newLoanType": "New Loan Type", "settings.name": "Name",
    "settings.description": "Description", "settings.requiredDocs": "Required Documents",
    "settings.typeName": "e.g. Vehicle Loan", "settings.typeDesc": "Brief description",
    "settings.docPlaceholder": "Type document name and press Enter",
    // Documents
    "doc.upload": "Upload Document", "doc.type": "Document Type",
    "doc.file": "File (PDF/JPG/PNG)", "doc.noDocuments": "No documents uploaded yet",
    // Customer Profile
    "profile.personalDetails": "Personal Details", "profile.contactAddress": "Contact & Address",
    "profile.fatherName": "Father's Name", "profile.dob": "Date of Birth",
    "profile.gender": "Gender", "profile.occupation": "Occupation",
    "profile.monthlyIncome": "Monthly Income", "profile.panNumber": "PAN Number",
    "profile.overview": "Overview", "profile.loans": "Loans", "profile.documents": "Documents",
    "profile.payments": "Payments", "profile.activity": "Activity",
    "profile.paymentHistory": "Payment History & Statements",
    // Proposals
    "proposal.proposals": "Proposals", "proposal.pending": "Pending",
    "proposal.approved": "Approved", "proposal.rejected": "Rejected",
    "proposal.approve": "Approve", "proposal.reject": "Reject",
    // Analytics
    "analytics.title": "Analytics", "analytics.loanPortfolio": "Loan Portfolio",
    // Misc
    "misc.noResultsFound": "No results found", "misc.comingSoon": "Coming soon",
    "misc.paidOn": "Paid", "misc.dOverdue": "d overdue",
  },
  hi: {
    // App
    "app.name": "Shubh Finance", "app.subtitle": "प्रबंधन प्रणाली",
    // Nav
    "nav.dashboard": "डैशबोर्ड", "nav.myLoans": "मेरे ऋण", "nav.emiSchedule": "EMI अनुसूची",
    "nav.payEMI": "EMI भुगतान", "nav.statements": "विवरण", "nav.myCustomers": "मेरे ग्राहक",
    "nav.proposals": "प्रस्ताव", "nav.analytics": "विश्लेषण", "nav.settings": "सेटिंग्स",
    "nav.users": "उपयोगकर्ता", "nav.customers": "ग्राहक", "nav.auditLogs": "ऑडिट लॉग",
    "nav.smsLogs": "SMS लॉग", "nav.logout": "लॉग आउट",
    "nav.collections": "संग्रह", "nav.collectPayment": "भुगतान संग्रह",
    "nav.myReceipts": "मेरी रसीदें", "nav.paymentChannels": "भुगतान चैनल",
    "nav.paymentVerification": "भुगतान सत्यापन", "nav.myPayments": "मेरे भुगतान",
    "nav.collectionIntelligence": "संग्रह इंटेलिजेंस", "nav.agentPerformance": "एजेंट प्रदर्शन",
    "nav.ledger": "ऋण बही खाता",
    "nav.overdueWaivers": "अतिदेय छूट", "nav.interestOnlyAccounts": "केवल ब्याज", "nav.partPaymentControls": "आंशिक भुगतान",
    "nav.disbursements": "वितरण", "nav.systemHealth": "सिस्टम स्वास्थ्य", "nav.accDashboard": "लेखा डैशबोर्ड", "nav.bankAccounts": "बैंक खाते", "nav.cashAccounts": "नकद खाते", "nav.journalEntries": "जर्नल प्रविष्टियाँ",
    // Roles
    "role.superadmin": "सुपर एडमिन", "role.admin": "एडमिन", "role.agent": "एजेंट", "role.customer": "ग्राहक",
    // Common
    "common.save": "सहेजें", "common.cancel": "रद्द करें", "common.close": "बंद करें", "common.delete": "हटाएं",
    "common.create": "बनाएं", "common.back": "वापस", "common.viewAll": "सभी देखें", "common.submit": "जमा करें",
    "common.loading": "लोड हो रहा है...", "common.search": "खोजें", "common.noData": "कोई डेटा नहीं मिला",
    "common.confirm": "पुष्टि करें", "common.yes": "हां", "common.no": "नहीं", "common.edit": "संपादित करें",
    "common.download": "डाउनलोड", "common.upload": "अपलोड", "common.refresh": "रीफ्रेश",
    "common.filter": "फ़िल्टर", "common.add": "जोड़ें", "common.saveChanges": "बदलाव सहेजें",
    "common.selectLoan": "ऋण चुनें", "common.language": "भाषा",
    "common.darkMode": "डार्क मोड", "common.lightMode": "लाइट मोड",
    // Auth
    "auth.login": "लॉगिन", "auth.username": "उपयोगकर्ता नाम", "auth.password": "पासवर्ड",
    "auth.loginBtn": "साइन इन करें", "auth.loginTitle": "वापस स्वागत है",
    "auth.loginSubtitle": "अपने खाते में साइन इन करें",
    // Status
    "status.active": "सक्रिय", "status.pending": "लंबित", "status.approved": "स्वीकृत",
    "status.rejected": "अस्वीकृत", "status.overdue": "अतिदेय", "status.paid": "भुगतान किया",
    "status.unpaid": "अवैतनिक", "status.closed": "बंद", "status.dueSoon": "जल्दी देय",
    "status.waived": "माफ़",
    // Customer Dashboard
    "custDash.myLoans": "मेरे ऋण", "custDash.welcomeBack": "वापस स्वागत है",
    "custDash.activeLoans": "सक्रिय ऋण", "custDash.nextEMI": "अगली EMI",
    "custDash.totalPaid": "कुल भुगतान", "custDash.overdueCharges": "अतिदेय शुल्क",
    "custDash.activeLoan": "सक्रिय ऋण", "custDash.noLoans": "अभी कोई ऋण नहीं",
    "custDash.contactAgent": "ऋण के लिए अपने एजेंट से संपर्क करें",
    "custDash.viewSchedule": "पूरी पुनर्भुगतान अनुसूची देखें",
    "custDash.downloadPDFCSV": "PDF / CSV डाउनलोड करें",
    "custDash.payNowSubtext": "लंबित",
    // Loan fields
    "loan.id": "ऋण ID", "loan.type": "प्रकार", "loan.principal": "मूलधन",
    "loan.emiAmount": "EMI राशि", "loan.frequency": "आवृत्ति", "loan.tenure": "अवधि",
    "loan.interestRate": "ब्याज दर", "loan.totalEmis": "कुल EMI",
    "loan.create": "ऋण बनाएं", "loan.startDate": "आरंभ तिथि", "loan.purpose": "उद्देश्य",
    "loan.customOverdueRate": "कस्टम अतिदेय दर (₹/दिन)",
    "loan.customOverdueRateHint": "प्रति-ग्राहक अतिदेय दर सेट करें। सिस्टम डिफ़ॉल्ट के लिए खाली छोड़ें।",
    "loan.interestRateLabel": "ब्याज दर (% प्रति माह)",
    "loan.principalLabel": "मूलधन (₹)", "loan.tenureLabel": "अवधि (महीने)",
    "loan.frequencyLabel": "EMI आवृत्ति", "loan.loanType": "ऋण प्रकार",
    "loan.months": "महीने", "loan.pm": "प्रति माह",
    "loan.paid": "भुगतान", "loan.total": "कुल",
    "loan.noLoans": "अभी कोई ऋण नहीं",
    // Frequencies
    "freq.daily": "दैनिक", "freq.monthly": "मासिक", "freq.bi_monthly": "15-15 दिन", "freq.quarterly": "त्रैमासिक",
    "freq.half_yearly": "छमाही", "freq.yearly": "वार्षिक",
    // EMI Schedule
    "emi.schedule": "EMI अनुसूची", "emi.viewSchedule": "अपनी पूरी पुनर्भुगतान अनुसूची देखें",
    "emi.number": "#", "emi.dueDate": "देय तिथि", "emi.amount": "EMI",
    "emi.principal": "मूलधन", "emi.interest": "ब्याज",
    "emi.overdueDays": "अतिदेय दिन", "emi.overdueCharges": "अतिदेय शुल्क",
    "emi.status": "स्थिति", "emi.daysLeft": "दिन बाकी", "emi.overdue": "अतिदेय",
    "emi.noSchedule": "कोई EMI अनुसूची नहीं मिली", "emi.fullSchedule": "पूर्ण अनुसूची",
    "emi.paidCount": "भुगतान किया", "emi.pendingCount": "लंबित", "emi.overdueCount": "अतिदेय",
    "emi.afterDue": "प्रतिदिन देय तिथि के बाद",
    "emi.action": "कार्रवाई", "emi.memo": "मेमो", "emi.markPaid": "भुगतान चिह्नित करें",
    // Pay EMI
    "pay.title": "EMI भुगतान", "pay.subtitle": "अपने आगामी भुगतान और अतिदेय शुल्क देखें",
    "pay.payNow": "अभी भुगतान करें", "pay.paymentDetails": "भुगतान विवरण",
    "pay.howToPay": "भुगतान कैसे करें", "pay.gotIt": "समझ गया", "pay.totalPayable": "कुल देय",
    "pay.emiAmount": "EMI राशि", "pay.allPaid": "सभी EMI भुगतान हो गए!",
    "pay.upToDate": "आपका ऋण अद्यतित है। शाबाश!",
    "pay.pendingEmis": "सभी लंबित EMI",
    "pay.step1": "1. भुगतान लेने के लिए अपने एजेंट से संपर्क करें",
    "pay.step2": "2. नकद, UPI, बैंक ट्रांसफर, या चेक से भुगतान करें",
    "pay.step3": "3. आपका एजेंट सिस्टम में भुगतान दर्ज करेगा",
    "pay.step4": "4. आपको SMS से पुष्टि मिलेगी",
    "pay.additionalPenalty": "प्रत्येक अतिरिक्त देरी के दिन के लिए अतिरिक्त जुर्माना लगाया जाएगा।",
    "pay.nextEMIDue": "अगली EMI देय", "pay.overdueEMI": "अतिदेय EMI",
    "pay.amount": "राशि", "pay.daysLeft": "दिन बाकी", "pay.totalDue": "कुल देय",
    // Overdue
    "overdue.penaltyInfo": "अतिदेय जुर्माना जानकारी",
    "overdue.penaltyText": "देय तिथि के बाद प्रत्येक दिन के लिए जुर्माना लगाया जाएगा।",
    "overdue.perDay": "/दिन", "overdue.immediateAttention": "अतिदेय EMI - तत्काल ध्यान आवश्यक",
    "overdue.haveOverdue": "आपकी अतिदेय EMI है/हैं",
    "overdue.totalCharges": "कुल शुल्क",
    "overdue.contactAgent": "तुरंत भुगतान के लिए अपने एजेंट से संपर्क करें।",
    "overdue.latePenalty": "विलंब शुल्क",
    "overdue.latePenaltyText": "देय तिथि के बाद प्रत्येक दिन के लिए दैनिक जुर्माना लागू है। यह दर आपके ऋण प्रशासक द्वारा निर्धारित है।",
    // Statements
    "stmt.title": "विवरण", "stmt.subtitle": "PDF या CSV में अपने ऋण भुगतान विवरण डाउनलोड करें",
    "stmt.noLoans": "कोई ऋण नहीं मिला",
    "stmt.pdfStatement": "PDF विवरण", "stmt.csvExport": "CSV निर्यात",
    "stmt.downloadPDF": "PDF", "stmt.downloadCSV": "CSV",
    "stmt.dateRange": "तिथि सीमा", "stmt.from": "से", "stmt.to": "तक",
    // Agent Dashboard
    "agent.myDashboard": "मेरा डैशबोर्ड", "agent.welcome": "स्वागत है",
    "agent.newCustomer": "नया ग्राहक", "agent.recentCustomers": "हाल के ग्राहक",
    "agent.upcomingEmis": "आगामी EMI", "agent.pendingProposals": "लंबित प्रस्ताव",
    "agent.customersOnboarded": "आपके द्वारा जोड़े गए", "agent.currentlyRunning": "वर्तमान में चल रहे",
    "agent.awaitingApproval": "अनुमोदन की प्रतीक्षा", "agent.proposalsApproved": "प्रस्ताव स्वीकृत",
    "agent.noCustomers": "अभी कोई ग्राहक नहीं। अपना पहला ग्राहक जोड़ें!",
    "agent.addCustomer": "ग्राहक जोड़ें", "agent.noUpcomingEmis": "कोई आगामी EMI नहीं",
    "agent.nextDueDates": "अगली देय तिथियां", "agent.noPendingProposals": "कोई लंबित प्रस्ताव नहीं",
    "agent.activeLoans": "सक्रिय ऋण", "agent.approved": "स्वीकृत",
    // Super Admin
    "sa.dashboard": "अवलोकन", "sa.totalCustomers": "कुल ग्राहक",
    "sa.activeLoans": "सक्रिय ऋण", "sa.totalOutstanding": "कुल बकाया",
    "sa.overdueLoans": "अतिदेय ऋण", "sa.recentLoans": "हाल के ऋण",
    "sa.agentPerformance": "एजेंट प्रदर्शन",
    // Admin
    "admin.dashboard": "डैशबोर्ड", "admin.myAgents": "मेरे एजेंट",
    "admin.totalCustomers": "कुल ग्राहक",
    // Settings
    "settings.title": "सेटिंग्स", "settings.subtitle": "सिस्टम-व्यापी सेटिंग्स कॉन्फ़िगर करें",
    "settings.overdueCharges": "अतिदेय शुल्क", "settings.enableOverdue": "अतिदेय शुल्क सक्षम करें",
    "settings.disableHint": "अक्षम होने पर, किसी भी ग्राहक पर शुल्क लागू नहीं होते",
    "settings.defaultRate": "डिफ़ॉल्ट दर (₹ प्रतिदिन)",
    "settings.workflow": "कार्यप्रवाह", "settings.requireApproval": "प्रस्ताव अनुमोदन आवश्यक",
    "settings.approvalHint": "नए ऋणों को सक्रियण से पहले सुपर एडमिन अनुमोदन की आवश्यकता है",
    "settings.rolePermissions": "भूमिका अनुमतियां",
    "settings.loanTypes": "ऋण प्रकार और दस्तावेज़", "settings.addType": "प्रकार जोड़ें",
    "settings.newLoanType": "नया ऋण प्रकार", "settings.name": "नाम",
    "settings.description": "विवरण", "settings.requiredDocs": "आवश्यक दस्तावेज़",
    "settings.typeName": "उदा. वाहन ऋण", "settings.typeDesc": "संक्षिप्त विवरण",
    "settings.docPlaceholder": "दस्तावेज़ नाम टाइप करें और Enter दबाएं",
    // Documents
    "doc.upload": "दस्तावेज़ अपलोड", "doc.type": "दस्तावेज़ प्रकार",
    "doc.file": "फ़ाइल (PDF/JPG/PNG)", "doc.noDocuments": "अभी तक कोई दस्तावेज़ अपलोड नहीं किया",
    // Customer Profile
    "profile.personalDetails": "व्यक्तिगत विवरण", "profile.contactAddress": "संपर्क और पता",
    "profile.fatherName": "पिता का नाम", "profile.dob": "जन्म तिथि",
    "profile.gender": "लिंग", "profile.occupation": "व्यवसाय",
    "profile.monthlyIncome": "मासिक आय", "profile.panNumber": "PAN नंबर",
    "profile.overview": "अवलोकन", "profile.loans": "ऋण", "profile.documents": "दस्तावेज़",
    "profile.payments": "भुगतान", "profile.activity": "गतिविधि",
    "profile.paymentHistory": "भुगतान इतिहास और विवरण",
    // Proposals
    "proposal.proposals": "प्रस्ताव", "proposal.pending": "लंबित",
    "proposal.approved": "स्वीकृत", "proposal.rejected": "अस्वीकृत",
    "proposal.approve": "स्वीकृत करें", "proposal.reject": "अस्वीकृत करें",
    // Analytics
    "analytics.title": "विश्लेषण", "analytics.loanPortfolio": "ऋण पोर्टफोलियो",
    // Misc
    "misc.noResultsFound": "कोई परिणाम नहीं मिला", "misc.comingSoon": "जल्द आ रहा है",
    "misc.paidOn": "भुगतान किया", "misc.dOverdue": "दिन अतिदेय",
  },
  pa: {
    // App
    "app.name": "Shubh Finance", "app.subtitle": "ਪ੍ਰਬੰਧਨ ਸਿਸਟਮ",
    // Nav
    "nav.dashboard": "ਡੈਸ਼ਬੋਰਡ", "nav.myLoans": "ਮੇਰੇ ਕਰਜ਼ੇ", "nav.emiSchedule": "EMI ਅਨੁਸੂਚੀ",
    "nav.payEMI": "EMI ਭੁਗਤਾਨ", "nav.statements": "ਬਿਆਨ", "nav.myCustomers": "ਮੇਰੇ ਗਾਹਕ",
    "nav.proposals": "ਪ੍ਰਸਤਾਵ", "nav.analytics": "ਵਿਸ਼ਲੇਸ਼ਣ", "nav.settings": "ਸੈਟਿੰਗਾਂ",
    "nav.users": "ਉਪਭੋਗਤਾ", "nav.customers": "ਗਾਹਕ", "nav.auditLogs": "ਆਡਿਟ ਲੌਗ",
    "nav.smsLogs": "SMS ਲੌਗ", "nav.logout": "ਲੌਗ ਆਉਟ",
    "nav.collections": "ਸੰਗ੍ਰਹਿ", "nav.collectPayment": "ਭੁਗਤਾਨ ਸੰਗ੍ਰਹਿ",
    "nav.myReceipts": "ਮੇਰੀਆਂ ਰਸੀਦਾਂ", "nav.paymentChannels": "ਭੁਗਤਾਨ ਚੈਨਲ",
    "nav.paymentVerification": "ਭੁਗਤਾਨ ਤਸਦੀਕ", "nav.myPayments": "ਮੇਰੇ ਭੁਗਤਾਨ",
    "nav.collectionIntelligence": "ਸੰਗ੍ਰਹਿ ਇੰਟੈਲੀਜੈਂਸ", "nav.agentPerformance": "ਏਜੰਟ ਪ੍ਰਦਰਸ਼ਨ",
    "nav.ledger": "ਕਰਜ਼ਾ ਖਾਤਾ",
    "nav.overdueWaivers": "ਬਕਾਇਆ ਛੋਟ", "nav.interestOnlyAccounts": "ਸਿਰਫ਼ ਵਿਆਜ", "nav.partPaymentControls": "ਅੰਸ਼ਕ ਭੁਗਤਾਨ",
    "nav.disbursements": "ਵੰਡ", "nav.systemHealth": "ਸਿਸਟਮ ਸਿਹਤ", "nav.accDashboard": "ਲੇਖਾ ਡੈਸ਼ਬੋਰਡ", "nav.bankAccounts": "ਬੈਂਕ ਖਾਤੇ", "nav.cashAccounts": "ਨਕਦ ਖਾਤੇ", "nav.journalEntries": "ਜਰਨਲ ਐਂਟਰੀਆਂ",
    // Roles
    "role.superadmin": "ਸੁਪਰ ਐਡਮਿਨ", "role.admin": "ਐਡਮਿਨ", "role.agent": "ਏਜੰਟ", "role.customer": "ਗਾਹਕ",
    // Common
    "common.save": "ਸੇਵ ਕਰੋ", "common.cancel": "ਰੱਦ ਕਰੋ", "common.close": "ਬੰਦ ਕਰੋ", "common.delete": "ਮਿਟਾਓ",
    "common.create": "ਬਣਾਓ", "common.back": "ਪਿੱਛੇ", "common.viewAll": "ਸਭ ਦੇਖੋ", "common.submit": "ਜਮ੍ਹਾ ਕਰੋ",
    "common.loading": "ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ...", "common.search": "ਖੋਜੋ", "common.noData": "ਕੋਈ ਡਾਟਾ ਨਹੀਂ ਮਿਲਿਆ",
    "common.confirm": "ਪੁਸ਼ਟੀ ਕਰੋ", "common.yes": "ਹਾਂ", "common.no": "ਨਹੀਂ", "common.edit": "ਸੋਧੋ",
    "common.download": "ਡਾਊਨਲੋਡ", "common.upload": "ਅੱਪਲੋਡ", "common.refresh": "ਤਾਜ਼ਾ ਕਰੋ",
    "common.filter": "ਫਿਲਟਰ", "common.add": "ਜੋੜੋ", "common.saveChanges": "ਬਦਲਾਅ ਸੇਵ ਕਰੋ",
    "common.selectLoan": "ਕਰਜ਼ਾ ਚੁਣੋ", "common.language": "ਭਾਸ਼ਾ",
    "common.darkMode": "ਡਾਰਕ ਮੋਡ", "common.lightMode": "ਲਾਈਟ ਮੋਡ",
    // Auth
    "auth.login": "ਲੌਗਇਨ", "auth.username": "ਉਪਭੋਗਤਾ ਨਾਮ", "auth.password": "ਪਾਸਵਰਡ",
    "auth.loginBtn": "ਸਾਈਨ ਇਨ ਕਰੋ", "auth.loginTitle": "ਵਾਪਸ ਸੁਆਗਤ ਹੈ",
    "auth.loginSubtitle": "ਆਪਣੇ ਖਾਤੇ ਵਿੱਚ ਸਾਈਨ ਇਨ ਕਰੋ",
    // Status
    "status.active": "ਸਰਗਰਮ", "status.pending": "ਬਕਾਇਆ", "status.approved": "ਮਨਜ਼ੂਰ",
    "status.rejected": "ਰੱਦ", "status.overdue": "ਮਿਆਦ ਪੁੱਗੀ", "status.paid": "ਭੁਗਤਾਨ ਕੀਤਾ",
    "status.unpaid": "ਅਦਾਇਗੀ ਨਹੀਂ", "status.closed": "ਬੰਦ", "status.dueSoon": "ਜਲਦੀ ਬਕਾਇਆ",
    "status.waived": "ਮੁਆਫ਼",
    // Customer Dashboard
    "custDash.myLoans": "ਮੇਰੇ ਕਰਜ਼ੇ", "custDash.welcomeBack": "ਵਾਪਸ ਸੁਆਗਤ ਹੈ",
    "custDash.activeLoans": "ਸਰਗਰਮ ਕਰਜ਼ੇ", "custDash.nextEMI": "ਅਗਲੀ EMI",
    "custDash.totalPaid": "ਕੁੱਲ ਭੁਗਤਾਨ", "custDash.overdueCharges": "ਮਿਆਦ ਪੁੱਗੇ ਖਰਚੇ",
    "custDash.activeLoan": "ਸਰਗਰਮ ਕਰਜ਼ਾ", "custDash.noLoans": "ਅਜੇ ਕੋਈ ਕਰਜ਼ਾ ਨਹੀਂ",
    "custDash.contactAgent": "ਕਰਜ਼ੇ ਲਈ ਆਪਣੇ ਏਜੰਟ ਨਾਲ ਸੰਪਰਕ ਕਰੋ",
    "custDash.viewSchedule": "ਪੂਰੀ ਭੁਗਤਾਨ ਅਨੁਸੂਚੀ ਦੇਖੋ",
    "custDash.downloadPDFCSV": "PDF / CSV ਡਾਊਨਲੋਡ ਕਰੋ",
    "custDash.payNowSubtext": "ਬਕਾਇਆ",
    // Loan fields
    "loan.id": "ਕਰਜ਼ਾ ID", "loan.type": "ਕਿਸਮ", "loan.principal": "ਮੂਲ ਰਕਮ",
    "loan.emiAmount": "EMI ਰਕਮ", "loan.frequency": "ਬਾਰੰਬਾਰਤਾ", "loan.tenure": "ਮਿਆਦ",
    "loan.interestRate": "ਵਿਆਜ ਦਰ", "loan.totalEmis": "ਕੁੱਲ EMI",
    "loan.create": "ਕਰਜ਼ਾ ਬਣਾਓ", "loan.startDate": "ਸ਼ੁਰੂ ਤਾਰੀਖ", "loan.purpose": "ਮਕਸਦ",
    "loan.customOverdueRate": "ਕਸਟਮ ਮਿਆਦ ਪੁੱਗੀ ਦਰ (₹/ਦਿਨ)",
    "loan.customOverdueRateHint": "ਪ੍ਰਤੀ-ਗਾਹਕ ਮਿਆਦ ਪੁੱਗੀ ਦਰ ਸੈੱਟ ਕਰੋ। ਡਿਫ਼ਾਲਟ ਲਈ ਖਾਲੀ ਛੱਡੋ।",
    "loan.interestRateLabel": "ਵਿਆਜ ਦਰ (% ਪ੍ਰਤੀ ਮਹੀਨਾ)",
    "loan.principalLabel": "ਮੂਲ ਰਕਮ (₹)", "loan.tenureLabel": "ਮਿਆਦ (ਮਹੀਨੇ)",
    "loan.frequencyLabel": "EMI ਬਾਰੰਬਾਰਤਾ", "loan.loanType": "ਕਰਜ਼ਾ ਕਿਸਮ",
    "loan.months": "ਮਹੀਨੇ", "loan.pm": "ਪ੍ਰਤੀ ਮਹੀਨਾ",
    "loan.paid": "ਭੁਗਤਾਨ", "loan.total": "ਕੁੱਲ",
    "loan.noLoans": "ਅਜੇ ਕੋਈ ਕਰਜ਼ਾ ਨਹੀਂ",
    // Frequencies
    "freq.daily": "ਰੋਜ਼ਾਨਾ", "freq.monthly": "ਮਹੀਨਾਵਾਰ", "freq.bi_monthly": "15-15 ਦਿਨ", "freq.quarterly": "ਤਿਮਾਹੀ",
    "freq.half_yearly": "ਛਿਮਾਹੀ", "freq.yearly": "ਸਾਲਾਨਾ",
    // EMI Schedule
    "emi.schedule": "EMI ਅਨੁਸੂਚੀ", "emi.viewSchedule": "ਆਪਣੀ ਪੂਰੀ ਭੁਗਤਾਨ ਅਨੁਸੂਚੀ ਦੇਖੋ",
    "emi.number": "#", "emi.dueDate": "ਬਕਾਇਆ ਤਾਰੀਖ", "emi.amount": "EMI",
    "emi.principal": "ਮੂਲ ਰਕਮ", "emi.interest": "ਵਿਆਜ",
    "emi.overdueDays": "ਮਿਆਦ ਪੁੱਗੇ ਦਿਨ", "emi.overdueCharges": "ਮਿਆਦ ਪੁੱਗੇ ਖਰਚੇ",
    "emi.status": "ਸਥਿਤੀ", "emi.daysLeft": "ਦਿਨ ਬਾਕੀ", "emi.overdue": "ਮਿਆਦ ਪੁੱਗੀ",
    "emi.noSchedule": "ਕੋਈ EMI ਅਨੁਸੂਚੀ ਨਹੀਂ ਮਿਲੀ", "emi.fullSchedule": "ਪੂਰੀ ਅਨੁਸੂਚੀ",
    "emi.paidCount": "ਭੁਗਤਾਨ ਕੀਤਾ", "emi.pendingCount": "ਬਕਾਇਆ", "emi.overdueCount": "ਮਿਆਦ ਪੁੱਗੀ",
    "emi.afterDue": "ਪ੍ਰਤੀ ਦਿਨ ਬਕਾਇਆ ਤਾਰੀਖ ਤੋਂ ਬਾਅਦ",
    "emi.action": "ਕਾਰਵਾਈ", "emi.memo": "ਮੈਮੋ", "emi.markPaid": "ਭੁਗਤਾਨ ਮਾਰਕ ਕਰੋ",
    // Pay EMI
    "pay.title": "EMI ਭੁਗਤਾਨ", "pay.subtitle": "ਆਪਣੇ ਆਉਣ ਵਾਲੇ ਭੁਗਤਾਨ ਅਤੇ ਮਿਆਦ ਪੁੱਗੇ ਖਰਚੇ ਦੇਖੋ",
    "pay.payNow": "ਹੁਣੇ ਭੁਗਤਾਨ ਕਰੋ", "pay.paymentDetails": "ਭੁਗਤਾਨ ਵੇਰਵੇ",
    "pay.howToPay": "ਭੁਗਤਾਨ ਕਿਵੇਂ ਕਰੋ", "pay.gotIt": "ਸਮਝ ਗਿਆ", "pay.totalPayable": "ਕੁੱਲ ਭੁਗਤਾਨਯੋਗ",
    "pay.emiAmount": "EMI ਰਕਮ", "pay.allPaid": "ਸਾਰੀਆਂ EMI ਭੁਗਤਾਨ ਹੋ ਗਈਆਂ!",
    "pay.upToDate": "ਤੁਹਾਡਾ ਕਰਜ਼ਾ ਅੱਪ ਟੂ ਡੇਟ ਹੈ। ਸ਼ਾਬਾਸ਼!",
    "pay.pendingEmis": "ਸਾਰੀਆਂ ਬਕਾਇਆ EMI",
    "pay.step1": "1. ਭੁਗਤਾਨ ਲੈਣ ਲਈ ਆਪਣੇ ਏਜੰਟ ਨਾਲ ਸੰਪਰਕ ਕਰੋ",
    "pay.step2": "2. ਨਕਦ, UPI, ਬੈਂਕ ਟ੍ਰਾਂਸਫਰ, ਜਾਂ ਚੈੱਕ ਰਾਹੀਂ ਭੁਗਤਾਨ ਕਰੋ",
    "pay.step3": "3. ਤੁਹਾਡਾ ਏਜੰਟ ਸਿਸਟਮ ਵਿੱਚ ਭੁਗਤਾਨ ਦਰਜ ਕਰੇਗਾ",
    "pay.step4": "4. ਤੁਹਾਨੂੰ SMS ਰਾਹੀਂ ਪੁਸ਼ਟੀ ਮਿਲੇਗੀ",
    "pay.additionalPenalty": "ਹਰ ਵਾਧੂ ਦੇਰੀ ਦੇ ਦਿਨ ਲਈ ਵਾਧੂ ਜੁਰਮਾਨਾ ਲਗਾਇਆ ਜਾਵੇਗਾ।",
    "pay.nextEMIDue": "ਅਗਲੀ EMI ਬਕਾਇਆ", "pay.overdueEMI": "ਮਿਆਦ ਪੁੱਗੀ EMI",
    "pay.amount": "ਰਕਮ", "pay.daysLeft": "ਦਿਨ ਬਾਕੀ", "pay.totalDue": "ਕੁੱਲ ਬਕਾਇਆ",
    // Overdue
    "overdue.penaltyInfo": "ਮਿਆਦ ਪੁੱਗੀ ਜੁਰਮਾਨਾ ਜਾਣਕਾਰੀ",
    "overdue.penaltyText": "ਬਕਾਇਆ ਤਾਰੀਖ ਤੋਂ ਬਾਅਦ ਹਰ ਦਿਨ ਲਈ ਜੁਰਮਾਨਾ ਲਗਾਇਆ ਜਾਵੇਗਾ।",
    "overdue.perDay": "/ਦਿਨ", "overdue.immediateAttention": "ਮਿਆਦ ਪੁੱਗੀ EMI - ਤੁਰੰਤ ਧਿਆਨ ਲੋੜੀਂਦਾ",
    "overdue.haveOverdue": "ਤੁਹਾਡੀ ਮਿਆਦ ਪੁੱਗੀ EMI ਹੈ/ਹਨ",
    "overdue.totalCharges": "ਕੁੱਲ ਖਰਚੇ",
    "overdue.contactAgent": "ਤੁਰੰਤ ਭੁਗਤਾਨ ਲਈ ਆਪਣੇ ਏਜੰਟ ਨਾਲ ਸੰਪਰਕ ਕਰੋ।",
    "overdue.latePenalty": "ਲੇਟ ਭੁਗਤਾਨ ਖਰਚੇ",
    "overdue.latePenaltyText": "ਬਕਾਇਆ ਤਾਰੀਖ ਤੋਂ ਬਾਅਦ ਹਰ ਦਿਨ ਲਈ ਰੋਜ਼ਾਨਾ ਜੁਰਮਾਨਾ ਲਾਗੂ ਹੈ। ਇਹ ਦਰ ਤੁਹਾਡੇ ਕਰਜ਼ਾ ਪ੍ਰਸ਼ਾਸਕ ਦੁਆਰਾ ਨਿਰਧਾਰਿਤ ਹੈ।",
    // Statements
    "stmt.title": "ਬਿਆਨ", "stmt.subtitle": "PDF ਜਾਂ CSV ਵਿੱਚ ਆਪਣੇ ਕਰਜ਼ਾ ਭੁਗਤਾਨ ਬਿਆਨ ਡਾਊਨਲੋਡ ਕਰੋ",
    "stmt.noLoans": "ਕੋਈ ਕਰਜ਼ਾ ਨਹੀਂ ਮਿਲਿਆ",
    "stmt.pdfStatement": "PDF ਬਿਆਨ", "stmt.csvExport": "CSV ਨਿਰਯਾਤ",
    "stmt.downloadPDF": "PDF", "stmt.downloadCSV": "CSV",
    "stmt.dateRange": "ਤਾਰੀਖ ਸੀਮਾ", "stmt.from": "ਤੋਂ", "stmt.to": "ਤੱਕ",
    // Agent Dashboard
    "agent.myDashboard": "ਮੇਰਾ ਡੈਸ਼ਬੋਰਡ", "agent.welcome": "ਸੁਆਗਤ ਹੈ",
    "agent.newCustomer": "ਨਵਾਂ ਗਾਹਕ", "agent.recentCustomers": "ਹਾਲੀਆ ਗਾਹਕ",
    "agent.upcomingEmis": "ਆਉਣ ਵਾਲੀਆਂ EMI", "agent.pendingProposals": "ਬਕਾਇਆ ਪ੍ਰਸਤਾਵ",
    "agent.customersOnboarded": "ਤੁਹਾਡੇ ਦੁਆਰਾ ਜੋੜੇ ਗਏ", "agent.currentlyRunning": "ਵਰਤਮਾਨ ਵਿੱਚ ਚੱਲ ਰਹੇ",
    "agent.awaitingApproval": "ਮਨਜ਼ੂਰੀ ਦੀ ਉਡੀਕ", "agent.proposalsApproved": "ਪ੍ਰਸਤਾਵ ਮਨਜ਼ੂਰ",
    "agent.noCustomers": "ਅਜੇ ਕੋਈ ਗਾਹਕ ਨਹੀਂ। ਆਪਣਾ ਪਹਿਲਾ ਗਾਹਕ ਜੋੜੋ!",
    "agent.addCustomer": "ਗਾਹਕ ਜੋੜੋ", "agent.noUpcomingEmis": "ਕੋਈ ਆਉਣ ਵਾਲੀ EMI ਨਹੀਂ",
    "agent.nextDueDates": "ਅਗਲੀਆਂ ਬਕਾਇਆ ਤਾਰੀਖਾਂ", "agent.noPendingProposals": "ਕੋਈ ਬਕਾਇਆ ਪ੍ਰਸਤਾਵ ਨਹੀਂ",
    "agent.activeLoans": "ਸਰਗਰਮ ਕਰਜ਼ੇ", "agent.approved": "ਮਨਜ਼ੂਰ",
    // Super Admin
    "sa.dashboard": "ਸੰਖੇਪ ਜਾਣਕਾਰੀ", "sa.totalCustomers": "ਕੁੱਲ ਗਾਹਕ",
    "sa.activeLoans": "ਸਰਗਰਮ ਕਰਜ਼ੇ", "sa.totalOutstanding": "ਕੁੱਲ ਬਕਾਇਆ",
    "sa.overdueLoans": "ਮਿਆਦ ਪੁੱਗੇ ਕਰਜ਼ੇ", "sa.recentLoans": "ਹਾਲੀਆ ਕਰਜ਼ੇ",
    "sa.agentPerformance": "ਏਜੰਟ ਪ੍ਰਦਰਸ਼ਨ",
    // Admin
    "admin.dashboard": "ਡੈਸ਼ਬੋਰਡ", "admin.myAgents": "ਮੇਰੇ ਏਜੰਟ",
    "admin.totalCustomers": "ਕੁੱਲ ਗਾਹਕ",
    // Settings
    "settings.title": "ਸੈਟਿੰਗਾਂ", "settings.subtitle": "ਸਿਸਟਮ-ਵਿਆਪੀ ਸੈਟਿੰਗਾਂ ਕੌਂਫਿਗਰ ਕਰੋ",
    "settings.overdueCharges": "ਮਿਆਦ ਪੁੱਗੇ ਖਰਚੇ", "settings.enableOverdue": "ਮਿਆਦ ਪੁੱਗੇ ਖਰਚੇ ਸਮਰੱਥ ਕਰੋ",
    "settings.disableHint": "ਅਯੋਗ ਹੋਣ 'ਤੇ, ਕਿਸੇ ਵੀ ਗਾਹਕ 'ਤੇ ਖਰਚੇ ਲਾਗੂ ਨਹੀਂ ਹੁੰਦੇ",
    "settings.defaultRate": "ਡਿਫ਼ਾਲਟ ਦਰ (₹ ਪ੍ਰਤੀ ਦਿਨ)",
    "settings.workflow": "ਕਾਰਜ-ਪ੍ਰਵਾਹ", "settings.requireApproval": "ਪ੍ਰਸਤਾਵ ਮਨਜ਼ੂਰੀ ਲੋੜੀਂਦੀ",
    "settings.approvalHint": "ਨਵੇਂ ਕਰਜ਼ਿਆਂ ਲਈ ਸਰਗਰਮ ਹੋਣ ਤੋਂ ਪਹਿਲਾਂ ਸੁਪਰ ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਲੋੜੀਂਦੀ ਹੈ",
    "settings.rolePermissions": "ਭੂਮਿਕਾ ਅਧਿਕਾਰ",
    "settings.loanTypes": "ਕਰਜ਼ਾ ਕਿਸਮਾਂ ਅਤੇ ਦਸਤਾਵੇਜ਼", "settings.addType": "ਕਿਸਮ ਜੋੜੋ",
    "settings.newLoanType": "ਨਵੀਂ ਕਰਜ਼ਾ ਕਿਸਮ", "settings.name": "ਨਾਮ",
    "settings.description": "ਵੇਰਵਾ", "settings.requiredDocs": "ਲੋੜੀਂਦੇ ਦਸਤਾਵੇਜ਼",
    "settings.typeName": "ਉਦਾ. ਵਾਹਨ ਕਰਜ਼ਾ", "settings.typeDesc": "ਸੰਖੇਪ ਵੇਰਵਾ",
    "settings.docPlaceholder": "ਦਸਤਾਵੇਜ਼ ਨਾਮ ਟਾਈਪ ਕਰੋ ਅਤੇ Enter ਦਬਾਓ",
    // Documents
    "doc.upload": "ਦਸਤਾਵੇਜ਼ ਅੱਪਲੋਡ", "doc.type": "ਦਸਤਾਵੇਜ਼ ਕਿਸਮ",
    "doc.file": "ਫ਼ਾਈਲ (PDF/JPG/PNG)", "doc.noDocuments": "ਅਜੇ ਤੱਕ ਕੋਈ ਦਸਤਾਵੇਜ਼ ਅੱਪਲੋਡ ਨਹੀਂ ਕੀਤਾ",
    // Customer Profile
    "profile.personalDetails": "ਨਿੱਜੀ ਵੇਰਵੇ", "profile.contactAddress": "ਸੰਪਰਕ ਅਤੇ ਪਤਾ",
    "profile.fatherName": "ਪਿਤਾ ਦਾ ਨਾਮ", "profile.dob": "ਜਨਮ ਤਾਰੀਖ",
    "profile.gender": "ਲਿੰਗ", "profile.occupation": "ਕਿੱਤਾ",
    "profile.monthlyIncome": "ਮਹੀਨਾਵਾਰ ਆਮਦਨ", "profile.panNumber": "PAN ਨੰਬਰ",
    "profile.overview": "ਸੰਖੇਪ", "profile.loans": "ਕਰਜ਼ੇ", "profile.documents": "ਦਸਤਾਵੇਜ਼",
    "profile.payments": "ਭੁਗਤਾਨ", "profile.activity": "ਗਤੀਵਿਧੀ",
    "profile.paymentHistory": "ਭੁਗਤਾਨ ਇਤਿਹਾਸ ਅਤੇ ਬਿਆਨ",
    // Proposals
    "proposal.proposals": "ਪ੍ਰਸਤਾਵ", "proposal.pending": "ਬਕਾਇਆ",
    "proposal.approved": "ਮਨਜ਼ੂਰ", "proposal.rejected": "ਰੱਦ",
    "proposal.approve": "ਮਨਜ਼ੂਰ ਕਰੋ", "proposal.reject": "ਰੱਦ ਕਰੋ",
    // Analytics
    "analytics.title": "ਵਿਸ਼ਲੇਸ਼ਣ", "analytics.loanPortfolio": "ਕਰਜ਼ਾ ਪੋਰਟਫੋਲੀਓ",
    // Misc
    "misc.noResultsFound": "ਕੋਈ ਨਤੀਜੇ ਨਹੀਂ ਮਿਲੇ", "misc.comingSoon": "ਜਲਦੀ ਆ ਰਿਹਾ ਹੈ",
    "misc.paidOn": "ਭੁਗਤਾਨ ਕੀਤਾ", "misc.dOverdue": "ਦਿਨ ਮਿਆਦ ਪੁੱਗੀ",
  },
};

const LANG_LABELS = { en: "English", hi: "हिन्दी", pa: "ਪੰਜਾਬੀ" };
const LANG_FLAGS = { en: "EN", hi: "हि", pa: "ਪੰ" };

const LanguageContext = createContext();

const languageKeys = Object.keys(translations);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("loanpro_lang") || "en");

  useEffect(() => { localStorage.setItem("loanpro_lang", lang); }, [lang]);

  const t = useCallback((key) => translations[lang]?.[key] || translations.en[key] || key, [lang]);

  const value = useMemo(() => ({
    lang, setLang, t, LANG_LABELS, LANG_FLAGS, languages: languageKeys,
  }), [lang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
